import { ApiSet, BaseResponse, IndexApiSet, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Dna, DnaForm, DnaSearchForm } from 'entities/dna'
import { TranscriptionForm } from 'components/shared/type'

type DnaListResponse = PagingResponse & {
    results: Dna[]
}

export function useFetchDnaListApi(searchForm: Form<DnaSearchForm>): IndexApiSet<DnaListResponse> & { execute: () => void } {
    const apiPath = 'dna/'
    const api = useIndexApi<DnaListResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

type DnaResponse = BaseResponse & {
    results: Dna
}

export function usePostDnaToDnaApi(): ApiSet<DnaResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'dna/to_dna/'
    const api = usePostApi<DnaResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function usePostToNgsApi(): ApiSet<DnaResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'dna/to_ngs/'
    const api = usePostApi<DnaResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function usePostDnaToBioanalyzerApi(): ApiSet<DnaResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'dna/to_bioanalyzer/'
    const api = usePostApi<DnaResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export type CreateSheetResponse = BaseResponse & {
    result?: {
        key?: string
    }
}

export function usePostCreateExperimentSheetApi(): ApiSet<CreateSheetResponse> & { execute: (form: Form<{ ids: string[] }>) => void } {
    const apiPath = 'dna/create_experiment_sheet/'
    const api = usePostApi<CreateSheetResponse, { ids: string[] }>(
        new CraifHttpClient(),
        {
            initialResponse: { result: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<{ ids: string[] }>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function usePostDnaToPcrApi(): ApiSet<DnaResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'dna/to_pcr/'
    const api = usePostApi<DnaResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function useDownloadCsvDnaApi(searchForm: Form<DnaSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'dna/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'dna', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePutDnaApi(): ApiSet<BaseResponse> & { execute: (form: Form<DnaForm>) => void } {
    const apiSet = usePutApi<BaseResponse, DnaForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<DnaForm>) => {
        const apiPath = `dna/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutDnaApi(): ApiSet<BaseResponse> & { execute: (form: Form<DnaForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, DnaForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<DnaForm[]>) => {
        const apiPath = `dna/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function usePostCreateAutoDispenseSheetApi(): ApiSet<CreateSheetResponse> & { execute: (form: Form<{ ids: string[] }>) => void } {
    const apiPath = 'dna/create_auto_dispense_sheet/'
    const api = usePostApi<CreateSheetResponse, { ids: string[] }>(
        new CraifHttpClient(),
        {
            initialResponse: { result: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<{ ids: string[] }>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}
